import { useEffect, useState } from "react";
import { getAllPosts } from "@/api/content";
import { getBrandConfig } from "@/config/brand";
import type { PostCard } from "@/types/content";
import ArticleCard from "@/components/base/ArticleCard";
import SectionSkeleton from "@/components/base/SectionSkeleton";

export default function LatestReviews() {
  const [articles, setArticles] = useState<PostCard[]>([]);
  const [loading, setLoading] = useState(true);
  const config = getBrandConfig().latestReviews;
  const global = getBrandConfig().global;

  useEffect(() => {
    getAllPosts().then((data) => {
      const sorted = data.sort(
        (a, b) =>
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      );
      setArticles(sorted.slice(0, 4));
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <h2 className="section-title">
              <i className="ri-article-line text-primary text-xl md:text-2xl w-6 h-6 flex items-center justify-center" />
              {config.title}
            </h2>
          </div>
          <SectionSkeleton type="cards" count={4} columns={4} surface="white" aspectRatio="16/10" footerLines={2} />
        </div>
      </section>
    );
  }

  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <div>
            <h2 className="section-title">
              <i className="ri-article-line text-primary text-xl md:text-2xl w-6 h-6 flex items-center justify-center" />
              {config.title}
            </h2>
            {config.subtitle && (
              <p className="text-sm text-slate-500 mt-1">{config.subtitle}</p>
            )}
          </div>
          <a
            href="/articles"
            className="text-sm text-slate-500 hover:text-primary transition-colors flex items-center gap-1 whitespace-nowrap"
          >
            {global.viewAll}
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {articles.map((article) => (
            <ArticleCard
              key={article.id}
              post={article}
              footer={
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" />
                    {article.publishedAt}
                  </span>
                  {article.rating && (
                    <span className="flex items-center gap-0.5 text-amber-500 font-medium">
                      <i className="ri-star-fill text-xs" />
                      {article.rating}
                    </span>
                  )}
                </div>
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
}